/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FormConfigCountdown } from './FormConfigCountdown';
import { FormHelperModal } from './FormHelperModal';
import { PreviewCountdown } from './PreviewCountdown';
import { validateUrl } from '../../../../shared/utils/validation';
import { MediaFieldCursor } from '../../CampaignComponents.types';
import { Item } from '../../../../shared/services/types/campaign';
import toast from '../../../../shared/components/Toast';
import { useCampaingCountdown } from '../../../../shared/store/useCampaignCountdown';
import useStore from '../../../../shared/store';

interface ModalCountdownConfigProps {
  disabled?: boolean;
  handleSave: (property: any) => void;
  mediaField: MediaFieldCursor;
}

const descriptionTextArea =
  'Promoção por tempo limitado e válida para compras realizadas no período em que houver a sinalização. Para mais informações, acesse o regulamento.';

export function ModalCountdowndConfig({
  disabled,
  handleSave,
  mediaField,
}: ModalCountdownConfigProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [defaultFormValues, setDefaultFormValues] = useState<Partial<Item>>({});
  const [endCampaingDate, setEndCampaignDate] = useState<Date | null>(new Date());
  const initialRef = useRef(null);
  const finalRef = useRef(null);
  const { currentDate, endDate, addEndDate, addDate } = useCampaingCountdown((state) => state);
  const { campaignId } = useParams();
  const { data: currentCampaign } = useStore((state) => state.campaigns.fetchCampaign);
  const { fetchCampaign } = useStore((state) => state.api);

  const methods = useForm({
    defaultValues: {
      label: defaultFormValues.countdown?.label || 'Essa promoção termina em:',
      backgroundColor: defaultFormValues.countdown?.backgroundColor || '#F5F5F5',
      fontColor: defaultFormValues.countdown?.fontColor || '#000000',
      startDate: defaultFormValues.countdown?.startDate || currentDate,
      endDate: defaultFormValues.countdown?.endDate || endCampaingDate,
      'title-modal': defaultFormValues.countdown?.helper?.title || 'Sobre a promoção',
      'description-modal': defaultFormValues.countdown?.helper?.description || descriptionTextArea,
      'button-link': defaultFormValues.countdown?.helper?.buttonLink || 'https://www.marisa.com.br',
      'button-text': defaultFormValues.countdown?.helper?.buttonText || 'Mais informações',
      hasUrl: false,
      hasHelperModal: false,
    },
  });

  const isMediaTypedCurrectly =
    methods.watch('button-link') && !validateUrl(methods.watch('button-link'));

  const handleSaveCountdown = () => {
    if (isMediaTypedCurrectly) {
      toast({ status: 'error', title: 'URL digitada incorretamente!' });
      return;
    }

    if (methods.watch('hasHelperModal')) {
      handleSave({
        label: `${methods.getValues('label')}`,
        startDate: `${methods.getValues('startDate')}`,
        endDate: `${methods.getValues('endDate')}`,
        backgroundColor: `${methods.getValues('backgroundColor')}`,
        fontColor: `${methods.getValues('fontColor')}`,
        helper: {
          title: `${methods.getValues('title-modal')}`,
          description: `${methods.getValues('description-modal')}`,
          buttonText: `${methods.getValues('button-text')}`,
          buttonLink: `${methods.getValues('hasUrl') ? '#' : methods.getValues('button-link')}`,
        },
      });
    } else {
      handleSave({
        label: `${methods.getValues('label')}`,
        startDate: `${methods.getValues('startDate')}`,
        endDate: `${methods.getValues('endDate')}`,
        backgroundColor: `${methods.getValues('backgroundColor')}`,
        fontColor: `${methods.getValues('fontColor')}`,
      });
    }

    onClose();
  };

  // Função para recuperar datas da campanha quando localStorage vazio
  const recoverCampaignDates = async () => {
    if (campaignId && (!currentDate || !endDate)) {
      try {
        // Busca a campanha atual se não estiver carregada
        if (!currentCampaign) {
          await fetchCampaign(campaignId);
        }

        // Se temos a campanha, sincroniza as datas
        if (currentCampaign) {
          const campaign = currentCampaign;
          if (campaign?.startDate && !currentDate) {
            addDate(campaign.startDate);
          }
          if (campaign?.endDate && !endDate) {
            addEndDate(campaign.endDate);
          }
        }
      } catch (error) {
        // Falha silenciosa - a aplicação continua funcionando
      }
    }
  };

  useEffect(() => {
    setDefaultFormValues(mediaField.item!);

    // Tenta recuperar data do localStorage primeiro
    const getEndDate = localStorage.getItem('@end-campaign-date');
    if (getEndDate && !endDate) {
      addEndDate(getEndDate);
    }

    // Se não conseguiu recuperar do localStorage, tenta buscar da API
    if (!getEndDate || !currentDate) {
      recoverCampaignDates();
    }

    setEndCampaignDate(new Date(String(getEndDate || endDate)));

    methods.reset({
      label: mediaField.item?.countdown?.label || 'Essa promoção termina em:',
      backgroundColor: mediaField.item?.countdown?.backgroundColor || '#F5F5F5',
      fontColor: mediaField.item?.countdown?.fontColor || '#000000',
      startDate: mediaField.item?.countdown?.startDate || currentDate,
      endDate: mediaField.item?.countdown?.endDate || new Date().toISOString(),
      'title-modal': mediaField.item?.countdown?.helper?.title || 'Sobre a promoção',
      'description-modal': mediaField.item?.countdown?.helper?.description || descriptionTextArea,
      'button-link': mediaField.item?.countdown?.helper?.buttonLink || 'https://www.marisa.com.br',
      'button-text': mediaField.item?.countdown?.helper?.buttonText || 'Mais informações',
    });
  }, [
    mediaField,
    endDate,
    addEndDate,
    currentDate,
    methods,
    campaignId,
    currentCampaign,
    fetchCampaign,
    addDate,
  ]);

  return (
    <>
      <Button
        mt="14px"
        background="primary.500"
        color="white"
        _hover={{ background: 'primary.600' }}
        onClick={() => onOpen()}
        disabled={disabled}
      >
        Configurar
      </Button>

      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isOpen}
        onClose={onClose}
        size="7xl"
      >
        <ModalOverlay />
        <FormProvider {...methods}>
          <ModalContent borderRadius="2xl" margin=" 10px" overflow="hidden">
            <ModalCloseButton />

            <form onSubmit={methods.handleSubmit(handleSaveCountdown)}>
              <ModalBody p={6}>
                <Grid templateColumns="repeat(3, 1fr)" gap="6">
                  <GridItem w="100%" px="1rem" borderRight="1px solid #EEE">
                    <FormConfigCountdown item={mediaField.item} />
                  </GridItem>

                  <GridItem w="100%" px="1rem" borderRight="1px solid #EEE">
                    <FormHelperModal item={mediaField.item} />
                  </GridItem>

                  <GridItem w="100%" px="1rem">
                    <PreviewCountdown item={mediaField.item} />
                  </GridItem>
                </Grid>
              </ModalBody>

              <ModalFooter borderTop="1px solid #eee">
                <Button
                  mt="14px"
                  background="primary.500"
                  color="white"
                  _hover={{ background: 'primary.600' }}
                  width="180px"
                  type="submit"
                >
                  Salvar
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </FormProvider>
      </Modal>
    </>
  );
}
